import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Spin } from 'antd'
import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import sharedClasses from '@modals/index.module.css'
import Button from '@UI/New/Button'
import { IcoRemove } from '@/utils/icons'
import { crmLeadContractApi } from '@/api'
import type { CrmLeadContract } from '@/api/types/crm/crm.types'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import AddContractDropdown from '@pages/Admin/AllUsers/modals/AddContractModal/AddContractDropdown'
import { getLeadFieldValue } from '@pages/Admin/CRM/utils/leadFields'
import type { LeadProfileLead } from '../../LeadProfileModal.types'
import LeadAddContractModal from './LeadAddContractModal'
import LeadAddTemplateContractModal from './LeadAddTemplateContractModal'
import LeadDocksList from './LeadDocksList'
import type { LeadContractDock } from './leadContracts.types'
import classes from './index.module.css'

type LeadContractsTabProps = {
  lead: LeadProfileLead
}

const mapContracts = (items: CrmLeadContract[]): LeadContractDock[] =>
  (items ?? []).map((item) => ({
    ...item,
    typeDocument: 'contract' as const,
  }))

const getLeadContractsSeed = (lead: LeadProfileLead): CrmLeadContract[] => {
  if ('contracts' in lead && Array.isArray(lead.contracts)) {
    return lead.contracts
  }
  return []
}

const getLeadUserContact = (
  user: LeadProfileLead['user'],
): {
  name?: string
  phone?: string
  parents: Array<{ id: number; name?: string | null; phone?: string | null }>
} => {
  if (!user || typeof user !== 'object') {
    return { parents: [] }
  }
  const raw = user as {
    name?: string | null
    phone?: string | null
    parents?: Array<{ id: number; name?: string | null; phone?: string | null }>
  }
  return {
    name: raw.name ?? undefined,
    phone: raw.phone ?? undefined,
    parents: Array.isArray(raw.parents) ? raw.parents : [],
  }
}

const LeadContractsTab: FC<LeadContractsTabProps> = ({ lead }) => {
  const addAlert = useAddAlert()
  const leadId = Number(lead.id)
  const linkedUserId =
    lead.user_id != null && Number(lead.user_id) > 0
      ? Number(lead.user_id)
      : null

  const [contracts, setContracts] = useState<LeadContractDock[]>(() =>
    mapContracts(getLeadContractsSeed(lead)),
  )
  const [loading, setLoading] = useState(false)
  const [addFileOpen, setAddFileOpen] = useState(false)
  const [addTemplateOpen, setAddTemplateOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)

  const userContact = useMemo(() => getLeadUserContact(lead.user), [lead.user])
  const leadName = String(
    getLeadFieldValue(lead, 'NAME') ?? userContact.name ?? '',
  )
  const leadPhone = String(
    getLeadFieldValue(lead, 'PHONE') ?? userContact.phone ?? '',
  )
  const parents = userContact.parents

  const refreshContracts = useCallback(async () => {
    if (!Number.isFinite(leadId) || leadId <= 0) return
    setLoading(true)
    try {
      const res = await crmLeadContractApi.getIndex({ lead_id: leadId })
      if (isError(res)) throw new Error()
      setContracts(mapContracts(res?.data?.data ?? []))
    } catch {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: 'Не удалось загрузить договоры',
      })
    } finally {
      setLoading(false)
    }
  }, [addAlert, leadId])

  useEffect(() => {
    void refreshContracts()
  }, [refreshContracts, linkedUserId])

  const handleConfirmDelete = async () => {
    if (deleteId == null) return
    setDeleting(true)
    try {
      const res = await crmLeadContractApi.delete({
        lead_id: leadId,
        id: deleteId,
      })
      if (isError(res)) throw new Error()
      setDeleteId(null)
      await refreshContracts()
      addAlert({
        type: 'success',
        title: 'Успешно',
        text: 'Договор удалён',
      })
    } catch {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: 'Не удалось удалить договор',
      })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <AddContractDropdown
          onAddFile={() => setAddFileOpen(true)}
          onAddTemplate={() => setAddTemplateOpen(true)}
        />
        {loading ? <Spin size="small" /> : null}
      </div>

      <p className={classes.hint}>
        Показаны договоры лида и привязанного ученика (если привязан). Это одни
        и те же документы, не копии.
      </p>

      <LeadDocksList
        leadId={leadId}
        contracts={contracts}
        leadName={leadName}
        leadPhone={leadPhone}
        linkedUserId={linkedUserId}
        parents={parents}
        onContractChanged={refreshContracts}
        onDeleteDock={(dock) => setDeleteId(dock.id)}
      />

      <LeadAddContractModal
        open={addFileOpen}
        leadId={leadId}
        onClose={() => setAddFileOpen(false)}
        onSuccess={refreshContracts}
      />

      <LeadAddTemplateContractModal
        open={addTemplateOpen}
        leadId={leadId}
        linkedUserId={linkedUserId}
        onClose={() => setAddTemplateOpen(false)}
        onSuccess={refreshContracts}
      />

      <Modal
        active={deleteId != null}
        setActive={() => {
          if (!deleting) setDeleteId(null)
        }}
        className={mclasses.modalWrapper as any}
        styleContent={{ minWidth: 'auto', padding: 0 }}
      >
        <div className={sharedClasses.modalContainer}>
          <div className={sharedClasses.modalHeader}>
            <div className={sharedClasses.modalTitleContainer}>
              <span className={sharedClasses.modalHeaderBigTitle}>
                Удалить договор
              </span>
            </div>
            <div
              className={mclasses.closeIcon}
              onClick={() => {
                if (!deleting) setDeleteId(null)
              }}
            >
              <IcoRemove />
            </div>
          </div>
          <div className={classes.confirmModalBody}>
            Договор будет удалён и у лида, и в ЛК ученика (если привязан). Это
            одна запись, не копия.
          </div>
          <div className={classes.confirmModalFooter}>
            <Button
              color="gray"
              disabled={deleting}
              onClick={() => setDeleteId(null)}
            >
              Отмена
            </Button>
            <Button
              color="danger"
              isLoading={deleting}
              onClick={() => void handleConfirmDelete()}
            >
              Удалить
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default LeadContractsTab
